/**
 * Add / Edit Workflow Step wizard (`AddWorkflowStepPopup`) — dirty-gate (ACME-1050).
 *
 * ## Standing product rule
 * Unsaved / leave: no real change → close silently (X / ESC). Prompt only when dirty.
 * Edit: Save stays disabled until dirty. Add: keep existing page/validity rules (first save).
 * View-only: never prompt.
 *
 * ## Why baseline compare (do not “simplify” to non-empty fields)
 * The old `isFormDirty()` treated any populated `stepName` / `functionName` as dirty.
 * On **Edit**, those fields are always filled after hydrate → useless Unsaved prompt on every X/ESC.
 * Same false-positive class as Workflow Cancel latching dirty from tab events.
 *
 * Correct pattern:
 * 1. Capture `captureWorkflowStepPopupBaseline(...)` **before the first `await`** in
 *    `connectedCallback`. The wizard is already interactive while function/source/region
 *    lookups run, and a null baseline reads as clean.
 * 2. After edit hydrate writes `formData` from `editData`, fold **only those
 *    hydrate-written fields** with `applyWorkflowStepPopupBaselineDefaults` — do
 *    not pass live `formData` that already includes typing from during the load
 *    (that swallows the edit and can pin Edit Save disabled). Restore in-flight
 *    edits onto the form after the fold so they remain visible and dirty.
 * 3. On close attempt, if `!isWorkflowStepPopupDirty(...)` → allow close with no dialog.
 * 4. Otherwise `openJhftDialog({ variant: "warning", ... })`.
 *
 * ## Safe changes
 * - Extend normalize fields when a new user-editable control should count as dirty.
 * - Keep omitting ephemeral UI (placeholders, validation flags, toast state).
 *
 * ## Unsafe changes
 * - Treating “field has any value” as dirty (breaks Edit).
 * - Recapturing live form state after an await (absorbs typing during load).
 * - Leaving the baseline null across function/source/region lookups.
 * - Opening the unsaved dialog without the dirty check.
 */

export type WorkflowStepPopupSnapshotInput = {
  formData?: Record<string, unknown> | null;
  /** File patterns shown on page 3 (may diverge from formData.FileMovePattern until save). */
  dataFileList?: unknown;
  /** Pending pattern text not yet added to the list. */
  dataFileName?: unknown;
  /** True after user clicks Replace key (edit SFTP SSH). */
  sftpSshKeyReplaceMode?: boolean;
};

function str(v: unknown): string {
  if (v == null) return "";
  return String(v).trim();
}

function boolish(v: unknown, defaultFalse = false): boolean {
  if (v === undefined || v === null || v === "") return defaultFalse;
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v.toLowerCase() !== "false" && v !== "0";
  return Boolean(v);
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function normalizeFileRename(fileRename: unknown): Record<string, string> {
  if (!fileRename || typeof fileRename !== "object") return {};
  const src = fileRename as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const k of Object.keys(src).sort()) {
    out[str(k)] = str(src[k]);
  }
  return out;
}

const SENSITIVE = ["Password", "privateKey", "PGPKey", "ConnectionString"] as const;

/**
 * Equality token for secrets. Change detection needs a stable compare, not plaintext
 * sitting on `_cancelBaseline` for the wizard lifetime.
 *
 * ponytail: djb2 token, not a KDF. Collision ceiling is a false-clean Cancel on two
 * different secrets with the same hash; upgrade to SHA-256 if this snapshot is ever
 * persisted or compared across sessions. Length is omitted so the baseline does not
 * disclose plaintext size.
 */
function fingerprint(v: string): string {
  let h = 5381;
  for (let i = 0; i < v.length; i++) h = Math.imul(h, 33) ^ v.charCodeAt(i);
  return `#${h >>> 0}`;
}

function secret(fd: Record<string, unknown>, key: (typeof SENSITIVE)[number]): string {
  return fingerprint(str(fd[key]));
}

/**
 * Stable JSON of user-meaningful step wizard state for close/Save compare.
 * Omits validation / ephemeral UI flags on purpose.
 */
export function captureWorkflowStepPopupBaseline(input: WorkflowStepPopupSnapshotInput): string {
  const fd = input.formData ?? {};
  const snapshot = {
    functionName: str(fd.functionName),
    stepName: str(fd.stepName),
    deleteSourceFile: boolish(fd.deleteSourceFile, false),
    locationType: str(fd.locationType),
    storageType: str(fd.storageType ?? fd.StorageType).toLowerCase(),
    path: str(fd.path),
    name: str(fd.name),
    bucketRename: str(fd.bucketRename),
    HostName: str(fd.HostName),
    Port: str(fd.Port),
    UserName: str(fd.UserName),
    Password: secret(fd, "Password"),
    AuthType: str(fd.AuthType),
    sourceAuthType: str(fd.sourceAuthType),
    destinationAuthType: str(fd.destinationAuthType),
    privateKey: secret(fd, "privateKey"),
    fileRenameKey: str(fd.fileRenameKey),
    fileRenameValue: str(fd.fileRenameValue),
    fileRename: normalizeFileRename(fd.fileRename),
    PGPKey: secret(fd, "PGPKey"),
    ConnectionString: secret(fd, "ConnectionString"),
    RoleARN: str(fd.RoleARN),
    Region: str(fd.Region),
    institutionUuid: str(fd.institutionUuid),
    FileMovePattern: asArray(fd.FileMovePattern ?? fd.fileMovePattern)
      .map(str)
      .slice()
      .sort(),
    dataFileList: asArray(input.dataFileList).map(str).slice().sort(),
    dataFileName: str(input.dataFileName),
    sftpSshKeyReplaceMode: Boolean(input.sftpSshKeyReplaceMode),
  };
  return JSON.stringify(snapshot);
}

const SNAPSHOT_EXTRA_KEYS = new Set(["dataFileList", "dataFileName", "sftpSshKeyReplaceMode"]);

/**
 * Fold values the load wrote itself (edit hydrate) into an existing baseline.
 *
 * Only keys the caller actually passed are patched. `captureWorkflowStepPopupBaseline`
 * always emits the full snapshot shape (empty strings for omitted fields); spreading
 * that onto the baseline would absorb in-flight user edits and zero unspecified keys.
 * Pass hydrate-written `formData` / extras — not live form state after the user typed.
 */
export function applyWorkflowStepPopupBaselineDefaults(
  baseline: string | null | undefined,
  defaults: WorkflowStepPopupSnapshotInput,
): string {
  let captured: Record<string, unknown>;
  try {
    captured = JSON.parse(
      baseline || captureWorkflowStepPopupBaseline({}),
    ) as Record<string, unknown>;
  } catch {
    captured = JSON.parse(captureWorkflowStepPopupBaseline({})) as Record<string, unknown>;
  }

  if (defaults.formData != null) {
    const provided = new Set(Object.keys(defaults.formData));
    if (provided.has("StorageType")) provided.add("storageType");
    if (provided.has("fileMovePattern")) provided.add("FileMovePattern");
    const incoming = JSON.parse(
      captureWorkflowStepPopupBaseline({ formData: defaults.formData }),
    ) as Record<string, unknown>;
    for (const key of Object.keys(incoming)) {
      if (SNAPSHOT_EXTRA_KEYS.has(key)) continue;
      if (provided.has(key)) captured[key] = incoming[key];
    }
  }
  if (defaults.dataFileList !== undefined) {
    captured.dataFileList = asArray(defaults.dataFileList).map(str).slice().sort();
  }
  if (defaults.dataFileName !== undefined) {
    captured.dataFileName = str(defaults.dataFileName);
  }
  if (defaults.sftpSshKeyReplaceMode !== undefined) {
    captured.sftpSshKeyReplaceMode = Boolean(defaults.sftpSshKeyReplaceMode);
  }
  return JSON.stringify(captured);
}

/** @returns false when baseline missing (treat as clean — close silently). */
export function isWorkflowStepPopupDirty(
  baseline: string | null | undefined,
  input: WorkflowStepPopupSnapshotInput,
): boolean {
  if (!baseline) return false;
  return captureWorkflowStepPopupBaseline(input) !== baseline;
}
